/**
 * InstallPrompt — small inline banner that surfaces the deferred
 * Chromium "install as app" prompt.
 *
 * Renders nothing on browsers that don't fire `beforeinstallprompt`
 * (Safari < 17, Firefox, every browser in the iOS WebKit fleet),
 * nothing once the user has already installed (the `appinstalled`
 * event clears the captured prompt), and nothing once the user
 * dismisses the banner via the inline ✕ button (persisted to
 * `localStorage` so the dismissal survives page reloads).
 *
 * The actual install machinery lives in `lib/installPrompt.ts` —
 * this component is the visual seam over that module so the React
 * tree stays free of `window.addEventListener` plumbing.
 *
 * Visual: pill-shaped action button matching the app's
 * `Button variant="primary"` styling, with a small `IconButton ✕`
 * dismiss affordance on the right edge. Without the ✕ a user who
 * never opens the native dialog (so never picks "Not now") would
 * see the banner on every page load until Chromium's own
 * eligibility heuristics suppress it.
 */

import { Box, Button, IconButton, Text } from "@reflecto/client-ui";
import { Download01, X } from "@untitledui/icons";
import { useCallback, useEffect, useState } from "react";
import { messages } from "../i18n";
import {
  dismissInstallPrompt,
  isInstallAvailable,
  subscribeInstallAvailability,
  triggerInstall,
} from "../lib/installPrompt";
import { logError } from "../lib/log";

export function InstallPrompt() {
  const [available, setAvailable] = useState(isInstallAvailable);

  useEffect(() => {
    // Re-read on subscribe to cover the case where the prompt was
    // captured between the initial render and effect commit.
    setAvailable(isInstallAvailable());
    return subscribeInstallAvailability(() => {
      setAvailable(isInstallAvailable());
    });
  }, []);

  const handleClick = useCallback(async (): Promise<void> => {
    try {
      await triggerInstall();
      // No state update needed — the `subscribeInstallAvailability`
      // listener below already flips `available` to false when the
      // captured prompt is consumed.
    } catch (err) {
      logError("ui", "install_prompt_failed", err);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    // Persists a flag so the banner stays hidden across reloads.
    // Subscriber notification fires inside `dismissInstallPrompt`
    // so the component re-renders without the banner.
    dismissInstallPrompt();
  }, []);

  if (!available) return null;

  return (
    <Box direction="row" align="center" justify="between" gap="md" paddingY="sm" paddingX="md">
      <Box direction="column" gap="xs">
        <Text size="m" weight="medium">
          {messages.install.title}
        </Text>
        <Text size="s" tone="secondary">
          {messages.install.body}
        </Text>
      </Box>
      <Box direction="row" align="center" gap="xs">
        <Button
          variant="primary"
          size="s"
          leadingIcon={Download01}
          onClick={() => {
            void handleClick();
          }}
        >
          {messages.install.cta}
        </Button>
        <IconButton
          icon={X}
          label={messages.install.dismissAria}
          size="s"
          onClick={handleDismiss}
        />
      </Box>
    </Box>
  );
}
